/**
 * Layout Configuration System
 * 
 * Defines which tiles appear on each page and their configuration overrides.
 * This allows pages to reuse the same tile components with different arrangements.
 */

/**
 * Available tile IDs (must match keys in TILE_CONFIG)
 */
export const TILE_IDS = {
  DECK_STATS: 'deckStats',
  CARD_SEARCH: 'cardSearch',
  RECOMMENDATIONS: 'recommendations',
  DRAW_SIMULATOR: 'drawSimulator',
  PRELOAD_DECKS: 'preloadDecks',
};

/**
 * Layout modes for the grid
 */
export const LAYOUT_MODES = {
  DEFAULT: 'default',
  EXPANDED: 'expanded',
  COLLAPSED: 'collapsed',
  FULL_WIDTH: 'fullWidth', // No sidebar tiles
};

/**
 * Page Layout Configurations
 * 
 * Each page defines:
 * - tiles: Array of tile IDs to render (in order)
 * - defaultLayoutMode: Initial grid layout mode
 * - allowLayoutToggle: Whether user can change layout mode
 * - deckAreaSpan: Override for deck area column span (optional)
 * - tileAreaSpan: Override for tile area column span (optional)
 * - overrides: Per-tile configuration overrides
 */
export const PAGE_LAYOUTS = {
  /**
   * Deck Builder - Full editing capabilities
   */
  deckBuilder: {
    id: 'deckBuilder',
    tiles: [
      TILE_IDS.DECK_STATS,
      TILE_IDS.CARD_SEARCH,
      TILE_IDS.RECOMMENDATIONS,
      TILE_IDS.DRAW_SIMULATOR,
    ],
    defaultLayoutMode: LAYOUT_MODES.DEFAULT,
    allowLayoutToggle: true,
    overrides: {
      [TILE_IDS.CARD_SEARCH]: {
        isPrimary: true,
        defaultCollapsed: false,
      },
      [TILE_IDS.DECK_STATS]: {
        defaultCollapsed: true,
      },
      [TILE_IDS.RECOMMENDATIONS]: {
        defaultCollapsed: true,
      },
      [TILE_IDS.DRAW_SIMULATOR]: {
        defaultCollapsed: true,
      },
    },
  },

  /**
   * Deck View - Read-only view of a deck
   * No card search or recommendations, just stats and simulator
   */
  deckView: {
    id: 'deckView',
    tiles: [
      TILE_IDS.DECK_STATS,
      TILE_IDS.DRAW_SIMULATOR,
    ],
    defaultLayoutMode: LAYOUT_MODES.DEFAULT,
    allowLayoutToggle: true,
    // Wider deck area since fewer tiles
    gridOverrides: {
      default: { deckArea: 8, tileArea: 2 },
      expanded: { deckArea: 6, tileArea: 4 },
      collapsed: { deckArea: 9, tileArea: 1 },
    },
    overrides: {
      [TILE_IDS.DECK_STATS]: {
        defaultCollapsed: false,
        defaultHeight: 4,
      },
      [TILE_IDS.DRAW_SIMULATOR]: {
        defaultCollapsed: false,
        defaultHeight: 3,
      },
    },
  },

  /**
   * Sample Decks Page - Grid of deck cards (no tile sidebar)
   */
  sampleDecks: {
    id: 'sampleDecks',
    tiles: [], // No sidebar tiles
    defaultLayoutMode: LAYOUT_MODES.FULL_WIDTH,
    allowLayoutToggle: false,
    gridOverrides: {
      fullWidth: { deckArea: 10, tileArea: 0 },
    },
  },

  /**
   * Card Gallery - Search and browse all cards
   */
  cardGallery: {
    id: 'cardGallery',
    tiles: [
      TILE_IDS.CARD_SEARCH,
    ],
    defaultLayoutMode: LAYOUT_MODES.EXPANDED,
    allowLayoutToggle: true,
    overrides: {
      [TILE_IDS.CARD_SEARCH]: {
        isPrimary: true,
        defaultCollapsed: false,
      },
    },
  },
};

/**
 * Get layout configuration for a page
 * @param {string} pageId - The page identifier
 * @returns {object} Layout configuration
 */
export const getPageLayout = (pageId) => {
  return PAGE_LAYOUTS[pageId] || PAGE_LAYOUTS.deckBuilder;
};

/**
 * Get tile configuration with page-specific overrides applied
 * @param {string} tileId - The tile identifier
 * @param {object} pageLayout - The page layout configuration
 * @param {object} baseTileConfig - Base tile config from TILE_CONFIG
 * @returns {object} Merged tile configuration
 */
export const getTileConfigForPage = (tileId, pageLayout, baseTileConfig) => {
  const baseConfig = baseTileConfig[tileId] || {};
  const overrides = pageLayout.overrides?.[tileId] || {};
  
  return {
    ...baseConfig,
    ...overrides,
  };
};

/**
 * Get grid spans for a layout mode with page overrides
 * @param {string} layoutMode - Current layout mode
 * @param {object} pageLayout - Page layout configuration
 * @param {object} defaultGridConfig - Default grid config from GRID_CONFIG
 * @returns {object} { deckArea, tileArea }
 */
export const getGridSpansForPage = (layoutMode, pageLayout, defaultGridConfig) => {
  // Check for page-specific grid overrides first
  if (pageLayout.gridOverrides?.[layoutMode]) {
    return pageLayout.gridOverrides[layoutMode];
  }
  
  // Fall back to default grid config
  const defaultLayout = defaultGridConfig.layouts[layoutMode] || defaultGridConfig.layouts.default;
  return {
    deckArea: defaultLayout.deckArea,
    tileArea: defaultLayout.tileArea,
  };
};

export default PAGE_LAYOUTS;
